<template>
	<div class="business-directory-danger-zone">
		<div class="card border-danger">
			<div class="card-header bg-danger text-white d-flex align-items-center">
				<font-awesome-icon :icon="['fas', 'exclamation-triangle']" class="me-2" />
				<strong>Danger Zone</strong>
			</div>
			<div class="card-body">
				<h6 class="mb-1">Delete all business directory entries</h6>
				<p class="text-muted small mb-3">
					This <strong>hard-deletes</strong> all business directory entries for this client (including any previously trashed ones — they will be wiped from the database, not just hidden).
					Optionally, also delete all categories and attached images.
					<strong>This action cannot be undone.</strong>
				</p>

				<div class="form-check mb-2">
					<input
						class="form-check-input"
						type="checkbox"
						v-model="includeCategories"
						id="bdDangerIncludeCategories"
						:disabled="working"
					/>
					<label class="form-check-label" for="bdDangerIncludeCategories">
						Also delete all categories
					</label>
				</div>

				<div class="form-check mb-3">
					<input
						class="form-check-input"
						type="checkbox"
						v-model="deleteImages"
						id="bdDangerDeleteImages"
						:disabled="working"
					/>
					<label class="form-check-label" for="bdDangerDeleteImages">
						Also delete attached images from media library
					</label>
					<small class="d-block text-muted">
						Only removes images that are not used by any other content (posts, pages, departments, news, FAQs, page builder, etc.). Images embedded inside HTML or JSON content cannot be auto-detected and may remain.
					</small>
				</div>

				<div class="mb-3">
					<label class="form-label small text-muted mb-1">Type <code>DELETE</code> to confirm</label>
					<input
						type="text"
						class="form-control form-control-sm"
						v-model="confirmText"
						placeholder="DELETE"
						:disabled="working"
						style="max-width: 240px"
					/>
				</div>

				<button
					class="btn btn-danger"
					:disabled="!canDelete || working"
					@click="confirmAndDelete"
				>
					<font-awesome-icon v-if="working" :icon="['fas', 'spinner']" class="fa-spin me-1" />
					<font-awesome-icon v-else :icon="['fas', 'trash']" class="me-1" />
					Delete all
				</button>

				<div v-if="result" class="alert alert-success mt-3 mb-0">
					Deleted <strong>{{ result.deleted_directories }}</strong> business directory entries<span v-if="result.deleted_categories">, <strong>{{ result.deleted_categories }}</strong> categories</span><span v-if="result.deleted_images"> and <strong>{{ result.deleted_images }}</strong> images</span>.
				</div>

				<div v-if="errorMessage" class="alert alert-danger mt-3 mb-0">
					{{ errorMessage }}
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import juniperApi from '@/api/juniperApi'

export default {
	name: 'BusinessDirectoryDangerZone',
	props: {
		clientId: {
			type: [Number, String],
			required: true,
		},
	},
	data() {
		return {
			includeCategories: false,
			deleteImages: false,
			confirmText: '',
			working: false,
			result: null,
			errorMessage: '',
		}
	},
	computed: {
		canDelete() {
			return this.confirmText.trim() === 'DELETE'
		},
	},
	methods: {
		async confirmAndDelete() {
			if (!this.canDelete) return
			const extras = []
			if (this.includeCategories) extras.push('all categories')
			if (this.deleteImages) extras.push('attached images from media library')
			const message = extras.length
				? `Permanently delete ALL business directory entries AND ${extras.join(' AND ')} for this client?`
				: 'Permanently delete ALL business directory entries for this client?'
			if (!window.confirm(message)) return

			this.working = true
			this.result = null
			this.errorMessage = ''
			try {
				const { data } = await juniperApi.post('/business-directories/delete-all', {
					client_id: this.clientId,
					confirm: 'DELETE',
					include_categories: this.includeCategories,
					delete_images: this.deleteImages,
				})
				this.result = data
				this.confirmText = ''
				this.$emit('deleted', data)
			} catch (e) {
				this.errorMessage = (e.response && e.response.data && e.response.data.message) || 'Failed to delete.'
			} finally {
				this.working = false
			}
		},
	},
}
</script>
